import { routes } from "@/helpers";
import { useExternalLinksQuery } from "@/services";
import { Menu } from "antd";

import { MenuItemType } from "@/enums/MenuItemType";
import { generateMenuItems } from "@/helpers/generateMenuItems";
import { useAllRights } from "@/hooks";

import { IViperMenuItem } from "@/interfaces/IViperMenuItem";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faArrowRightArrowLeft,
  faBook,
  faCalendarClock,
  faCalendarDays,
  faEnvelopes,
  faKey,
  faMemoCircleCheck,
  faPenField,
  faPeopleGroup,
  faShieldKeyhole,
  faSquareList,
  faUser,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";

export const NavMenu = () => {
  const { adminRights, hasSomeAdminRights } = useAllRights();
  const intl = useIntl();
  const { data: externalLinks } = useExternalLinksQuery({});

  const items: IViperMenuItem[] = [
    {
      key: "dashboard",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.dashboard" }),
      url: routes.dashboard,
    },
    {
      key: "projects",
      type: MenuItemType.Item,
      title: intl.formatMessage({ id: "navbar.projects" }),
      url: routes.projects.list,
    },
    {
      key: "goTo",
      type: MenuItemType.Parent,
      title: intl.formatMessage({ id: "navbar.goTo" }),
      children: [
        {
          key: "goTo:GRS",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.grs" }),
          externalSite: true,
          url: externalLinks?.globalPrototypeRequestSystemUrl,
        },
        {
          key: "goTo:LRS",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.lrs" }),
          externalSite: true,
          url: externalLinks?.labRequestSystemUrl,
        },
        {
          key: "goTo:TimeTracker",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.timeTracker" }),
          externalSite: true,
          url: externalLinks?.timeTrackerUrl,
        },
        {
          key: "goTo:PowerBI",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.powerBI" }),
          externalSite: true,
          url: externalLinks?.powerBIUrl,
        },
      ],
    },
    {
      key: "admin",
      type: MenuItemType.Parent,
      title: intl.formatMessage({ id: "navbar.administration" }),
      visible: hasSomeAdminRights,
      children: [
        {
          key: "admin:project:group",
          type: MenuItemType.Group,
          title: intl.formatMessage({ id: "navbar.projects" }),
        },
        {
          key: "admin:projectRoles",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.projectRoles" }),
          visible: adminRights.canViewProjectRoles,
          icon: <FontAwesomeIcon icon={faPeopleGroup as IconProp} />,
          url: routes.admin.projectRoles.list,
        },
        {
          key: "admin:projectTemplates",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.projectTemplates" }),
          visible: adminRights.canViewTemplates,
          icon: <FontAwesomeIcon icon={faSquareList as IconProp} />,
          url: routes.admin.projectTemplates.list,
        },
        {
          key: "admin:approvalWorkFlows",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.approvalWorkFlows" }),
          visible: adminRights.canViewApprovalWorkFlows,
          icon: <FontAwesomeIcon icon={faMemoCircleCheck as IconProp} />,
          url: routes.admin.approvalWorkFlows.list,
        },
        {
          key: "admin:baselineDates",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.baselineDates" }),
          visible: adminRights.canViewBaselineDates,
          icon: <FontAwesomeIcon icon={faCalendarClock as IconProp} />,
          url: routes.admin.baselineDates.list,
        },
        {
          key: "admin:adminConfig",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.adminConfig" }),
          visible: adminRights.canViewBaselineDates,
          icon: <FontAwesomeIcon icon={faCalendarDays as IconProp} />,
          url: routes.admin.adminConfig.list,
        },
        {
          key: "admin:userManagement:group",
          type: MenuItemType.Group,
          title: intl.formatMessage({ id: "navbar.userManagement" }),
        },
        {
          key: "admin:userManagement:users",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.users" }),
          visible: adminRights.canViewUsers,
          icon: <FontAwesomeIcon icon={faUser as IconProp} />,
          url: routes.admin.users.list,
        },
        {
          key: "admin:userManagement:permissions",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.permissions" }),
          visible: adminRights.canViewPermissions,
          icon: <FontAwesomeIcon icon={faKey as IconProp} />,
          url: routes.admin.permissions.list,
        },
        {
          key: "admin:userManagement:conditionalAccess",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.conditionalAccess" }),
          visible: adminRights.canViewPermissions,
          icon: <FontAwesomeIcon icon={faShieldKeyhole as IconProp} />,
          url: routes.admin.conditionalAccess.list,
        },
        {
          key: "admin:userManagement:userReplacement",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.userReplacement" }),
          visible: adminRights.canViewPermissions,
          icon: <FontAwesomeIcon icon={faArrowRightArrowLeft as IconProp} />,
          url: routes.admin.userReplacement.view,
        },
        {
          key: "admin:userManagement:notificationGroups",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.notificationGroups" }),
          visible: adminRights.canViewNotificationGroups,
          icon: <FontAwesomeIcon icon={faEnvelopes as IconProp} />,
          url: routes.admin.notificationGroups.list,
        },
        {
          key: "admin:misc:group",
          type: MenuItemType.Group,
          title: intl.formatMessage({ id: "navbar.misc" }),
        },
        {
          key: "admin:misc:apiDocumentation",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.apiDocumentation" }),
          visible: adminRights.canViewApiDocumentation,
          icon: <FontAwesomeIcon icon={faBook as IconProp} />,
          url: routes.admin.apiDocumentation,
        },
        {
          key: "admin:misc:forms",
          type: MenuItemType.Item,
          title: intl.formatMessage({ id: "navbar.forms" }),
          visible: adminRights.canViewForms,
          icon: <FontAwesomeIcon icon={faPenField as IconProp} />,
          url: routes.admin.forms.list,
        },
      ],
    },
  ];

  return (
    <Menu
      data-test-id="nav-menu"
      mode="horizontal"
      selectedKeys={[]}
      items={generateMenuItems(items)}
      disabledOverflow={true}
    />
  );
};
